import { clx } from "@hm/fundamental"

import type { FilterOption } from "../../model/types"
import { SelectDropdown } from "./select-dropdown"
import { SelectSearch } from "./select-search"
import { SelectTrigger } from "./select-trigger"
import { useControlledSelect } from "./useControlledSelect"

interface ControlledSelectProps {
	name: string
	label: string
	options: FilterOption[]
	value: string | number | undefined
	onChange: (value: string | number) => void
	disabled?: boolean
	searchable?: boolean
	className?: string
	dropdownClassName?: string
	searchAriaLabel?: string
	closeAriaLabel?: string
	"data-cy"?: string
}

export const ControlledSelect: React.FC<ControlledSelectProps> = ({
	name,
	label,
	options,
	value,
	onChange,
	disabled = false,
	searchable = true,
	className,
	dropdownClassName,
	searchAriaLabel = "Search",
	closeAriaLabel = "Close",
	"data-cy": dataCy = "controlled-select",
}) => {
	const {
		isOpen,
		searchQuery,
		deferredQuery,
		filteredOptions,
		containerRef,
		searchInputRef,
		triggerRef,
		listboxId,
		selectedLabel,
		selectedIcon,
		toggleDropdown,
		closeDropdown,
		handleSearchChange,
		handleKeyDown,
		handleSelect,
	} = useControlledSelect({
		options,
		value,
		onChange,
		disabled,
		label,
		dataCy: `${dataCy}-${name}`,
	})

	return (
		<div
			ref={containerRef}
			className={clx("relative", className)}
			data-cy={dataCy}
		>
			{isOpen && searchable ? (
				<SelectSearch
					searchInputRef={searchInputRef}
					searchQuery={searchQuery}
					placeholder={label}
					onSearchChange={handleSearchChange}
					onKeyDown={handleKeyDown}
					onClose={closeDropdown}
					searchAriaLabel={searchAriaLabel}
					closeAriaLabel={closeAriaLabel}
					data-cy={dataCy}
				/>
			) : (
				<SelectTrigger
					triggerRef={triggerRef}
					label={label}
					selectedLabel={selectedLabel}
					selectedIcon={selectedIcon}
					isOpen={isOpen}
					disabled={disabled}
					listboxId={listboxId}
					onToggle={toggleDropdown}
					onKeyDown={handleKeyDown}
					data-cy={dataCy}
				/>
			)}

			{isOpen && (
				<SelectDropdown
					listboxId={listboxId}
					deferredQuery={deferredQuery}
					filteredOptions={filteredOptions}
					value={value}
					dropdownClassName={dropdownClassName}
					onSelect={handleSelect}
					data-cy={dataCy}
				/>
			)}
		</div>
	)
}
